import { AxiosResponse } from 'axios';
import { IValidationApiResponse } from '../../Services/FormikValidatorService';
import { getRequest, postRequest } from '../../Services/HttpService';
import { IGroupSelectOptions } from '../Forms/CmdField';
import { ITagifyItem } from '../Forms/Fields/ContentEditableTextarea2';
import {
  accessoriesKeys,
  alarmKeys,
  checkboxKeys,
  doorsKeys,
  indicationKeys,
  operationKeys,
  positionKeys,
  rangeName,
  rideKeys,
  securityKeys,
  vehicleKeys,
  waypointsKeys,
} from './constants';
import { ILabelInputs } from './types';

export const getValidationAll = async (): Promise<IValidationApiResponse | false> => {
  const response: IValidationApiResponse | AxiosResponse | false = await getRequest(
    '/v1/alarm/rule/validation'
  );
  return response as IValidationApiResponse | false;
};

export const getValidation = async (indexId: string): Promise<IValidationApiResponse | false> => {
  const response: IValidationApiResponse | AxiosResponse | false =
    await getRequest<IValidationApiResponse>(`/v1/alarm/rule/validation/${indexId}`);
  return response as IValidationApiResponse | false;
};

export const getRules = async (
  rideownershipValue?: string,
  ridePublicityValue?: string,
  minPendigValue?: number
): Promise<IGroupSelectOptions | null> => {
  let data = {};

  data = {
    rideOwnership: rideownershipValue,
    ridePrivacy: ridePublicityValue,
    minPending: minPendigValue,
  };

  const response: AxiosResponse | false = await postRequest('/v1/alarm/form/conditions', data, {
    returnAxiosResponse: true,
  });

  if (response && response.data && response.status === 200) {
    return response.data;
  }
  return null;
};

export const getData = async (indexId: string): Promise<IGroupSelectOptions | null> => {
  const response: AxiosResponse | false = await getRequest(`/v1/alarm/rule/form/${indexId}`, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    return response.data;
  }
  return null;
};
export const getLabels = (labelIdentificator: string): ILabelInputs => {
  switch (labelIdentificator) {
    case 'waypoint_entered':
      return {
        indexIdMin: 'alarms.modal.rule.waypoint_entered.label',
        type: 'waypoints',
        typeName: labelIdentificator,
      };

    case 'waypoint_left':
      return {
        indexIdMin: 'alarms.modal.rule.waypoint_left.label',
        type: 'waypoints',
        typeName: labelIdentificator,
      };

    case 'waypoint_in':
      return {
        indexIdMin: 'alarms.modal.rule.waypoint_in.label',
        type: 'waypoints',
        typeName: labelIdentificator,
      };

    case 'waypoint_out':
      return {
        indexIdMin: 'alarms.modal.rule.waypoint_out.label',
        type: 'waypoints',
        typeName: labelIdentificator,
      };

    case 'bin_2':
      return {
        indexIdMin: 'alarms.modal.rule.bin_2.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'bin_3':
      return {
        indexIdMin: 'alarms.modal.rule.bin_3.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'analog_1':
      return {
        indexIdMin: 'alarms.modal.rule.analog_1.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'analog_2':
      return {
        indexIdMin: 'alarms.modal.rule.analog_2.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'ignition':
      return {
        indexIdMin: 'alarms.modal.rule.ignition.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'doors':
      return {
        indexIdMin: 'alarms.modal.rule.doors.label',
        type: 'binary_group',
        typeName: labelIdentificator,
      };

    case 'movement':
      return {
        indexIdMin: 'alarms.modal.rule.movement.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'ride_privacy':
      return {
        indexIdMin: 'alarms.modal.rule.ride_privacy.label',
        type: 'binary',
        typeName: labelIdentificator,
      };

    case 'engine_rpm':
      return {
        indexIdMin: 'alarms.modal.rule.engine_rpm.min.text',
        indexIdMax: 'alarms.modal.rule.engine_rpm.max.text',
        customLabel: 'alarms.modal.rule.engine_rpm.customLabel',
        placeholder: 'alarms.modal.rule.engine_rpm.placeholder',
        unit: 'ot/min',
        type: 'range',
      };

    case 'vehicle_speed':
      return {
        indexIdMin: 'alarms.modal.rule.vehicle_speed.min.text',
        indexIdMax: 'alarms.modal.rule.vehicle_speed.max.text',
        customLabel: 'alarms.modal.rule.vehicle_speed.customLabel',
        placeholder: 'alarms.modal.rule.vehicle_speed.placeholder',
        unit: 'km/h',
        type: 'range',
      };

    case 'engine_temperature':
      return {
        indexIdMin: 'alarms.modal.rule.engine_temperature.min.text',
        indexIdMax: 'alarms.modal.rule.engine_temperature.max.text',
        customLabel: 'alarms.modal.rule.engine_temperature.customLabel',
        placeholder: 'alarms.modal.rule.engine_temperature.placeholder',
        unit: '˚C',
        type: 'range',
      };

    case 'fuel_level_liters':
      return {
        indexIdMin: 'alarms.modal.rule.fuel_level_liters.min.text',
        indexIdMax: 'alarms.modal.rule.fuel_level_liters.max.text',
        customLabel: 'alarms.modal.rule.fuel_level_liters.customLabel',
        placeholder: 'alarms.modal.rule.fuel_level_liters.placeholder',
        unit: 'L',
        type: 'range',
      };

    case 'fuel_level_perc':
      return {
        indexIdMin: 'alarms.modal.rule.fuel_level_perc.min.text',
        indexIdMax: 'alarms.modal.rule.fuel_level_perc.max.text',
        customLabel: 'alarms.modal.rule.fuel_level_perc.customLabel',
        placeholder: 'alarms.modal.rule.fuel_level_perc.placeholder',
        unit: '%',
        type: 'range',
      };

    case 'battery_voltage':
      return {
        indexIdMin: 'alarms.modal.rule.battery_voltage.min.text',
        indexIdMax: 'alarms.modal.rule.battery_voltage.max.text',
        customLabel: 'alarms.modal.rule.battery_voltage.customLabel',
        placeholder: 'alarms.modal.rule.battery_voltage.placeholder',
        unit: 'V',
        type: 'range',
      };

    case 'throttle_position':
      return {
        indexIdMin: 'alarms.modal.rule.throttle_position.min.text',
        indexIdMax: 'alarms.modal.rule.throttle_position.max.text',
        customLabel: 'alarms.modal.rule.throttle_position.customLabel',
        placeholder: 'alarms.modal.rule.throttle_position.placeholder',
        unit: '%',
        type: 'range',
      };

    case 'tachometer':
      return {
        indexIdMin: 'alarms.modal.rule.tachometer.min.text',
        indexIdMax: 'alarms.modal.rule.tachometer.max.text',
        customLabel: 'alarms.modal.rule.tachometer.customLabel',
        placeholder: 'alarms.modal.rule.tachometer.placeholder',
        unit: 'km',
        type: 'range',
      };

    case 'temp1':
      return {
        indexIdMin: 'alarms.modal.rule.temp1.min.text',
        indexIdMax: 'alarms.modal.rule.temp1.max.text',
        customLabel: 'enum.alarms.inputDefinitions.inputs.temp1',
        placeholder: 'alarms.modal.rule.temp1.placeholder',
        unit: '˚C',
        type: 'range',
      };

    case 'temp2':
      return {
        indexIdMin: 'alarms.modal.rule.temp2.min.text',
        indexIdMax: 'alarms.modal.rule.temp2.max.text',
        customLabel: 'enum.alarms.inputDefinitions.inputs.temp2',
        placeholder: 'alarms.modal.rule.temp2.placeholder',
        unit: '˚C',
        type: 'range',
      };

    case 'satellites_count':
      return {
        indexIdMin: 'alarms.modal.rule.satellites_count.min.text',
        indexIdMax: 'alarms.modal.rule.satellites_count.max.text',
        customLabel: 'alarms.modal.rule.satellites_count.customLabel',
        placeholder: 'alarms.modal.rule.satellites_count.placeholder',
        unit: '',
        type: 'range',
      };

    case 'altitude':
      return {
        indexIdMin: 'alarms.modal.rule.altitude.min.text',
        indexIdMax: 'alarms.modal.rule.altitude.max.text',
        customLabel: 'alarms.modal.rule.altitude.customLabel',
        placeholder: 'alarms.modal.rule.altitude.placeholder',
        unit: 'm n.m.',
        type: 'range',
      };

    case 'eco_drive':
      return {
        indexIdMin: 'alarms.modal.rule.eco_drive.min.text',
        indexIdMax: 'alarms.modal.rule.eco_drive.max.text',
        customLabel: 'alarms.modal.rule.eco_drive.customLabel',
        placeholder: 'alarms.modal.rule.eco_drive.placeholder',
        unit: '',
        type: 'range',
      };
    case 'ev_battery_level':
      return {
        indexIdMin: 'alarms.modal.rule.ev_battery_level.min',
        indexIdMax: 'alarms.modal.rule.ev_battery_level.max',
        customLabel: 'alarms.modal.rule.ev_battery_level.label',
        placeholder: 'alarms.modal.rule.fuel_level_perc.placeholder',
        unit: '%',
        type: 'range',
      };
    case 'ev_range':
      return {
        indexIdMin: 'alarms.modal.rule.ev_range.min',
        indexIdMax: 'alarms.modal.rule.ev_range.max',
        customLabel: 'alarms.modal.rule.ev_range.label',
        placeholder: 'alarms.modal.rule.tachometer.placeholder',
        unit: 'km',
        type: 'range',
      };
    case 'adblue_level_perc':
      return {
        indexIdMin: 'alarms.modal.rule.fuel_level_perc.min.text',
        indexIdMax: 'alarms.modal.rule.fuel_level_perc.max.text',
        customLabel: 'graph.adBlue.page.title',
        placeholder: 'alarms.modal.rule.fuel_level_perc.placeholder',
        unit: '%',
        type: 'range',
      };
    case 'ev_charging_start':
      return {
        indexIdMin: 'alarms.modal.rule.ev_charging_start.label',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'ev_charging_end':
      return {
        indexIdMin: 'alarms.modal.rule.ev_charging_end.label',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'ev_plugged_in':
      return {
        indexIdMin: 'alarms.modal.rule.ev_plugged_in.label',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'ev_plugged_out':
      return {
        indexIdMin: 'alarms.modal.rule.ev_plugged_out.label',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'esp_off':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.esp_off',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'driver_seatbelt':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.driver_seatbelt',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'jamming':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.jamming',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'factory_emulated_alarm':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.factory_emulated_alarm',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'car_remote_lock':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.car_remote_lock',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'auto_geofence':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.auto_geofence',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'unplug':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.unplug',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'coolant_level_low':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.coolant_level_low',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'oil_level_low':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.oil_level_low',
        type: 'binary',
        typeName: labelIdentificator,
      };
    case 'check_engine':
      return {
        indexIdMin: 'enum.alarms.inputDefinitions.inputs.check_engine',
        type: 'binary',
        typeName: labelIdentificator,
      };

    default:
      return {
        indexIdMin: '',
        indexIdMax: '',
        customLabel: '',
        placeholder: '',
      };
  }
};
export const sortOptionsByKeys = (options: ITagifyItem[], keys: string[]) => {
  // Sort the filtered options based on the order of keysForSort
  const sortedOptions = options.sort((a, b) => keys.indexOf(a.value) - keys.indexOf(b.value));

  return sortedOptions;
};
export type OptionTag = {
  item: string;
  value: string;
};
export type ConfigurationTag = {
  title: string;
  height?: string;
  filter: (option: OptionTag) => void;
  keys: string[];
  id: string;
};

export const selectConfigurations: ConfigurationTag[] = [
  {
    title: 'alarm.form.notificationMessageTags.alarmTitle',
    height: 'auto',
    filter: (option) => alarmKeys.includes(option.value),
    keys: alarmKeys,
    id: 'alarm',
  },
  {
    title: 'alarm.form.notificationMessageTags.vehicleTitle',
    // height: '170px',
    filter: (option) => vehicleKeys.includes(option.value),
    keys: vehicleKeys,
    id: 'vehicle',
  },

  {
    title: 'alarm.form.notificationMessageTags.rideTitle',
    height: '86px',
    filter: (option) => rideKeys.includes(option.value),
    keys: rideKeys,
    id: 'ride',
  },
  {
    title: 'alarm.form.notificationMessageTags.indicationTitle',
    height: '34px',
    filter: (option) => indicationKeys.includes(option.value),
    keys: indicationKeys,
    id: 'indication',
  },
  {
    title: 'alarm.form.notificationMessageTags.positionNavigationTitle',
    // height: '170px',
    filter: (option) => positionKeys.includes(option.value),
    keys: positionKeys,
    id: 'position',
  },
  {
    title: 'alarm.form.notificationMessageTags.operationTitle',
    // height: 'auto',
    filter: (option) => operationKeys.includes(option.value),
    keys: operationKeys,
    id: 'operation',
  },
  {
    title: 'alarm.form.notificationMessageTags.accessoriesTitle',
    height: '34px',
    filter: (option) => accessoriesKeys.includes(option.value),
    keys: accessoriesKeys,
    id: 'accessories',
  },
  {
    title: 'alarm.form.notificationMessageTags.securityTitle',
    height: '34px',
    filter: (option) => securityKeys.includes(option.value),
    keys: securityKeys,
    id: 'security',
  },
];
export const tagsTitles = [
  {
    item: 'enum.alarms.notificationMessageTags.waypoint',
    value: 'waypoint',
  },
  {
    item: 'enum.alarms.notificationMessageTags.time',
    value: 'time',
  },
  {
    item: 'enum.alarms.notificationMessageTags.date_time',
    value: 'date_time',
  },
  {
    item: 'enum.alarms.notificationMessageTags.date',
    value: 'date',
  },
  {
    item: 'enum.alarms.notificationMessageTags.vehicleBatteryRange',
    value: 'vehicleBatteryRange',
  },
  {
    item: 'enum.alarms.notificationMessageTags.eco_drive',
    value: 'eco_drive',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.licenseNumber',
    value: 'licence_number',
  },
  {
    item: 'enum.alarms.notificationMessageTags.fuel_level_perc',
    value: 'fuel_level_perc',
  },
  {
    item: 'enum.alarms.notificationMessageTags.fuel_level_liters',
    value: 'fuel_level_liters',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.driverName',
    value: 'driver_name',
  },
  {
    item: 'enum.alarms.notificationMessageTags.ev_charging_start',
    value: 'ev_charging_start',
  },
  {
    item: 'enum.alarms.notificationMessageTags.ev_battery_level',
    value: 'ev_battery_level',
  },
  {
    item: 'enum.alarms.notificationMessageTags.altitude',
    value: 'altitude',
  },
  {
    item: 'enum.alarms.notificationMessageTags.waypoint_in',
    value: 'waypoint_in',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.mileage',
    value: 'mileage',
  },
  {
    item: 'enum.alarms.notificationMessageTags.battery_voltage',
    value: 'battery_voltage',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.alarmName',
    value: 'alarm_name',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.vehicleName',
    value: 'vehicle_name',
  },
  {
    item: 'enum.alarms.notificationMessageTags.vehicle_name_licence_number',
    value: 'vehicle_name_licence_number',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.mapLink',
    value: 'map_link',
  },
  {
    item: 'enum.alarms.notificationMessageTags.waypoint_left',
    value: 'waypoint_left',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.engineRpm',
    value: 'engine_rpm',
  },
  {
    item: 'enum.alarms.notificationMessageTags.movement',
    value: 'movement',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.doorsOpen',
    value: 'doors',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.throttlePosition',
    value: 'throttle_position',
  },
  {
    item: 'enum.alarms.notificationMessageTags.ev_plugged_in',
    value: 'ev_plugged_in',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.speed',
    value: 'vehicle_speed',
  },
  {
    item: 'enum.alarms.notificationMessageTags.satellites_count',
    value: 'satellites_count',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.ignition',
    value: 'ignition',
  },
  {
    item: 'enum.alarms.notificationMessageTags.temp_1',
    value: 'temp_1',
  },
  {
    item: 'enum.alarms.notificationMessageTags.temp_2',
    value: 'temp_2',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.engineTemperature',
    value: 'engine_temperature',
  },
  {
    item: 'enum.alarms.notificationMessageTags.ride_privacy',
    value: 'ride_privacy',
  },
  {
    item: 'enum.alarms.notificationMessageTags.waypoint_entered',
    value: 'waypoint_entered',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.longitude',
    value: 'longitude',
  },
  {
    item: 'alarms.notificationMessage.templateVariables.latitude',
    value: 'latitude',
  },
  {
    item: 'enum.alarms.notificationMessageTags.adblue_level_perc',
    value: 'adblue_level_perc',
  },
  {
    item: 'enum.alarms.notificationMessageTags.coolant_level',
    value: 'coolant_level',
  },
  {
    item: 'enum.alarms.notificationMessageTags.driver_seatbelt',
    value: 'driver_seatbelt',
  },
  {
    item: 'enum.alarms.notificationMessageTags.emulated_alarm',
    value: 'emulated_alarm',
  },
  {
    item: 'enum.alarms.notificationMessageTags.esp_off',
    value: 'esp_off',
  },
  {
    item: 'enum.alarms.notificationMessageTags.geofence',
    value: 'geofence',
  },
  {
    item: 'enum.alarms.notificationMessageTags.check_engine',
    value: 'check_engine',
  },
  {
    item: 'enum.alarms.notificationMessageTags.jamming',
    value: 'jamming',
  },
  {
    item: 'enum.alarms.notificationMessageTags.oil_level',
    value: 'oil_level',
  },
  {
    item: 'enum.alarms.notificationMessageTags.remote_lock',
    value: 'remote_lock',
  },
  {
    item: 'enum.alarms.notificationMessageTags.unplug',
    value: 'unplug',
  },
];

export const isRangeCondition = (condition: string) => {
  return rangeName.includes(condition);
};
export const isWaypointCondition = (condition: string) => {
  return waypointsKeys.includes(condition);
};
export const isCheckboxGroupCondition = (condition: string) => {
  return doorsKeys.includes(condition);
};
export const isCheckboxCondition = (condition: string) => {
  return checkboxKeys.includes(condition);
};
